import axios from 'axios';
import { useAuthStore } from '../store/auth';

const baseURL = import.meta.env.VITE_API_BASE ?? '/v1';

export const api = axios.create({ baseURL });

// ---------------------------------------------------------------------------
// Uploaded-file URL origin handling — works in BOTH dev and prod.
//
// The backend serves uploads at `<apiPrefix>/files/...` and returns/stores them
// as host-RELATIVE paths (e.g. "/v1/files/product-image/x.png"). In dev the app
// and API share an origin (Vite proxy), so relative paths load fine. In prod the
// frontend (bio.airmax.co.in) and API (bioback.airmax.co.in) are DIFFERENT
// origins, so a relative "/v1/files/..." in an <img src> resolves against the
// frontend origin → 404.
//
// We keep stored URLs RELATIVE (portable — no domain baked into the DB) and
// translate at the network edge: every RESPONSE gets the API origin prepended to
// file paths (so images/PDFs load cross-origin), every REQUEST gets it stripped
// (so nothing absolute is ever persisted). Both are no-ops in dev, where the API
// base is relative and FILE_ORIGIN is empty.
const API_IS_ABSOLUTE = /^https?:\/\//i.test(baseURL);
const FILE_ORIGIN = API_IS_ABSOLUTE ? new URL(baseURL).origin : '';
const API_PATH = API_IS_ABSOLUTE ? new URL(baseURL).pathname : baseURL; // e.g. "/v1"
const FILES_PREFIX = `${API_PATH.replace(/\/+$/, '')}/files/`;          // e.g. "/v1/files/"
const ABS_FILES_PREFIX = `${FILE_ORIGIN}${FILES_PREFIX}`;

/**
 * Make an uploaded-file path absolute against the API origin. Use for the rare
 * spot that builds an image/file URL outside an API response (most render sites
 * don't need it — the response interceptor below already absolutizes URLs in
 * fetched data). Safe for empty / already-absolute / data: / blob: values.
 */
export function fileUrl(u?: string | null): string {
  if (!u) return '';
  if (FILE_ORIGIN && u.startsWith(FILES_PREFIX)) return FILE_ORIGIN + u;
  return u;
}

/** Recursively prepend the API origin to any "<prefix>/files/..." string in a
 *  parsed JSON payload. Only touches strings that START with the relative files
 *  prefix, so already-absolute URLs are left untouched (no double-prefixing). */
function absolutizeFileUrls(node: unknown): void {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      const v = node[i];
      if (typeof v === 'string') { if (v.startsWith(FILES_PREFIX)) node[i] = FILE_ORIGIN + v; }
      else absolutizeFileUrls(v);
    }
    return;
  }
  const obj = node as Record<string, unknown>;
  for (const k in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
    const v = obj[k];
    if (typeof v === 'string') { if (v.startsWith(FILES_PREFIX)) obj[k] = FILE_ORIGIN + v; }
    else absolutizeFileUrls(v);
  }
}

api.interceptors.request.use((cfg) => {
  const token = useAuthStore.getState().accessToken;
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  // Persist file URLs as RELATIVE paths: strip the API origin we add on responses
  // so an edited entity never round-trips an absolute URL back into the DB.
  // Skip FormData (uploads) and other non-JSON bodies; clone so we never mutate
  // caller/React state, and only when an absolute file URL is actually present.
  if (FILE_ORIGIN && cfg.data && typeof cfg.data === 'object' && !(cfg.data instanceof FormData)) {
    try {
      const json = JSON.stringify(cfg.data);
      if (json.includes(ABS_FILES_PREFIX)) {
        cfg.data = JSON.parse(json.split(ABS_FILES_PREFIX).join(FILES_PREFIX));
      }
    } catch {
      /* non-serialisable body — leave untouched */
    }
  }
  return cfg;
});

let isRefreshing = false;
let pendingQueue: ((t: string) => void)[] = [];

api.interceptors.response.use(
  (r) => {
    if (FILE_ORIGIN) absolutizeFileUrls(r.data);
    return r;
  },
  async (err) => {
    const { response, config } = err;
    if (response?.status === 401 && !config._retried) {
      const refresh = useAuthStore.getState().refreshToken;
      if (!refresh) {
        useAuthStore.getState().logout();
        return Promise.reject(err);
      }
      if (isRefreshing) {
        return new Promise((resolve) => {
          pendingQueue.push((token) => {
            config.headers.Authorization = `Bearer ${token}`;
            resolve(api(config));
          });
        });
      }
      isRefreshing = true;
      try {
        const r = await axios.post(`${baseURL}/auth/refresh`, { refreshToken: refresh });
        const data = r.data?.data ?? r.data;
        // Raw axios here bypasses the response interceptor, so absolutize the
        // user's profilePhotoUrl ourselves before it's persisted to the store.
        if (FILE_ORIGIN) absolutizeFileUrls(data);
        useAuthStore.getState().setTokens(data.accessToken, data.refreshToken, data.user);
        pendingQueue.forEach((cb) => cb(data.accessToken));
        pendingQueue = [];
        config.headers.Authorization = `Bearer ${data.accessToken}`;
        config._retried = true;
        return api(config);
      } catch (e) {
        useAuthStore.getState().logout();
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(err);
  },
);

// Unwrap the backend response envelope.
//
// The API wraps every response as { success, data, error, requestId, timestamp }.
// Paginated endpoints additionally carry a top-level `meta` ({ page, pageSize,
// total }). List pages and entity dropdowns consume paginated results as
// `{ data: rows[], meta }` (they read `.data` for rows and `.meta.total` for the
// count), so for paginated envelopes we return exactly that shape. For every
// other response we return the bare `data` payload (array or object), which is
// what non-paginated callers (e.g. /roles, /territories, detail pages) expect.
export async function request<T = any>(opts: Parameters<typeof api.request>[0]): Promise<T> {
  const r = await api.request(opts);
  const body = r.data;
  if (body && typeof body === 'object' && 'success' in body) {
    if ('meta' in body && body.meta != null) {
      return { data: body.data, meta: body.meta } as T;
    }
    // Inside the envelope, the payload IS body.data — even when it's null
    // (e.g. /advances/me/active → no active advance). Returning the raw envelope
    // here would hand callers a truthy object with no real fields (→ NaN/undefined).
    return (body.data ?? null) as T;
  }
  return body as T;
}
